import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Button, Card, Divider, Flex, Stack, Switch } from '@mantine/core';
import { Text, TextType } from 'ui-components/components/text';

import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';

import { EventSourceStrings } from '../../../../../../tracking/tracking-constants';
import { activateStore, getRetailerConfiguration, updateEnhancedReturnsStoreStatus } from '../../../../../../store/returns/returns.api';
import { STORE_ACTIVATION_MODE_BASIC } from '../../../../../../store/returns/returns.helpers';
import { refreshStoreList } from '../../../../../../store/seller/seller.actions';
import { useStoreUpdateContext } from '../../context';
import { ReadOnlyPortalUrl } from '../read-only-portal-url';
import useTrackReturnsSettings from '../hooks/use-track-returns-settings';
import { useReturnsTabContext } from '../hooks/use-returns-tab-context';
import Global from '../../../../../../store/global-selectors';
import type { ReturnSettings } from '../types.d';
import { ReturnLayout } from './components/return-layout';
import { ReturnPolicy } from './components/return-policy';
import { CustomerShippingOptions } from './components/customer-shipping-options/customer-shipping-options';
import { ReturnsPanelHeader } from './components/returns-panel-header/returns-panel-header';
import type { RetailerConfig } from './hooks/use-enhanced-mode-activation';

import { messages } from './messages';
import styles from './shipstation-returns-panel.module.less';

const RETURNS_GROUP_NAME = 'returns';
const CHECKBOX_ID = 'use-shipstation-returns-toggle';

type ShipstationReturnsPanelProps = {
  returnSettings: ReturnSettings;
};

export const ShipstationReturnsPanel = ({ returnSettings }: ShipstationReturnsPanelProps) => {
  const { useBrandedReturns } = returnSettings;
  const { updateStoreSetting } = useStoreUpdateContext();
  const { storeGuid } = useParams();
  const dispatch = useDispatch();
  const intl = useIntl();
  const trackReturns = useTrackReturnsSettings(EventSourceStrings.STORE_SETTINGS_RETURNS_TAB);
  const [retailerConfig, setRetailerConfig] = useState<RetailerConfig | undefined>(undefined);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isConfigFetchingError, setIsConfigFetchingError] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);

  const fetchRetailerConfig = useCallback(async () => {
    if (storeGuid) {
      try {
        setIsLoadingConfig(true);
        setIsConfigFetchingError(false);
        setRetailerConfig(undefined);
        const data = await getRetailerConfiguration(storeGuid);
        setRetailerConfig(data);
      } catch {
        setIsConfigFetchingError(true);
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.configFetchingError} />,
        });
      } finally {
        setIsLoadingConfig(false);
      }
    }
  }, [storeGuid]);

  const handleToggle = useCallback(
    (isChecked: boolean) => {
      updateStoreSetting(RETURNS_GROUP_NAME, 'useBrandedReturns', isChecked);
    },
    [updateStoreSetting],
  );

  const handleActivateStore = useCallback(() => {
    if (!storeGuid) return;

    setActivationError(null);
    setIsActivating(true);

    activateStore(storeGuid, { mode: STORE_ACTIVATION_MODE_BASIC })
      .then(async () => {
        // Sync status from SSR - this updates the database from "AuthenticationInitiated" to "Authenticated"
        await updateEnhancedReturnsStoreStatus(storeGuid);

        setIsActivating(false);

        // Show success notification
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: <FormattedMessage {...messages.activateStoreSuccess} />,
        });

        // Reload store configuration
        await fetchRetailerConfig();

        // Refresh store list in the background to update context
        dispatch(refreshStoreList());
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.Errors?.[0]?.Message || intl.formatMessage(messages.activateStoreError);

        setIsActivating(false);
        setActivationError(errorMessage);

        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: errorMessage,
        });
      });
  }, [storeGuid, dispatch, intl, fetchRetailerConfig]);

  useEffect(() => {
    fetchRetailerConfig();
  }, [fetchRetailerConfig]);

  const { isShipstationReturnsEnabled, isStoreSetupCompleteForEnhancedReturns } = useReturnsTabContext();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const store = useSelector((state: any) => Global.selectSellerStoreData(state, { storeGuid }));
  const isMarketplaceReturnsExcluded = store?.isShipstationReturnsExcluded;

  if (!isShipstationReturnsEnabled) {
    return null;
  }

  return (
    <ReturnLayout isLoading={isLoadingConfig}>
      {!isConfigFetchingError && (
        <>
          <ReturnPolicy onReload={fetchRetailerConfig} retailerConfig={retailerConfig!} />
          <Card padding='md' radius={6} withBorder>
            <Stack gap={0}>
              {isStoreSetupCompleteForEnhancedReturns && (
                <Flex align='center' justify='space-between'>
                  <ReturnsPanelHeader
                    descriptionMessage={messages.enableBrandedReturnPortalDescription}
                    titleMessage={messages.enableBrandedReturnPortal}
                  />
                  <Switch checked={!!useBrandedReturns} id={CHECKBOX_ID} onChange={(event) => handleToggle(event.currentTarget.checked)} />
                </Flex>
              )}

              {!isStoreSetupCompleteForEnhancedReturns && (
                <Stack gap='1.5'>
                  <Flex align='center' justify='space-between'>
                    <ReturnsPanelHeader descriptionMessage={messages.activateStoreDescription} titleMessage={messages.activateStoreTitle} />
                    <Button loading={isActivating} onClick={handleActivateStore}>
                      <FormattedMessage {...messages.activateStoreButton} />
                    </Button>
                  </Flex>
                  {activationError && (
                    <BannerMessageV2 content={<Text textType={TextType.Paragraph} value={activationError} />} status={BannerStatus.Error} />
                  )}
                </Stack>
              )}

              {isMarketplaceReturnsExcluded && (
                <BannerMessageV2
                  className={styles['marketplace-excluded-banner']}
                  content={
                    <Text
                      textType={TextType.Paragraph}
                      value={useBrandedReturns ? messages.marketplaceExcludedPortalOn : messages.marketplaceExcludedPortalOff}
                    />
                  }
                  status={BannerStatus.Info}
                />
              )}

              {isStoreSetupCompleteForEnhancedReturns && useBrandedReturns && (
                <>
                  <Divider my='md' />
                  <ReadOnlyPortalUrl
                    labelMessage={messages.advancedReturnsPortalUrlLabel}
                    reminderMessage={messages.portalUrlReminder}
                    trackCopyUrl={trackReturns?.trackPortalUrlCopied}
                    url={retailerConfig?.customer_portal_domains?.[0] ?? ''}
                    urlClassName={styles.url}
                    urlWrapperClassName={styles.urlWrapper}
                  />
                </>
              )}
            </Stack>
          </Card>
          <CustomerShippingOptions returnSettings={returnSettings} />
        </>
      )}
    </ReturnLayout>
  );
};
